import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Observable, switchMap, take } from 'rxjs';

import { UserApiService } from 'src/app/api/services/user-api.service';

import { Feed } from '../models/core.models';
import { selectUser } from '../state';

/**
 * Reading one feed by id.
 *
 * **`FeedState.feed` is not a cached read** and deliberately stays NgRx:
 * `loadFeedSuccess` seeds it once, a dozen-plus local edits write it afterwards
 * (selection counts, view properties, the view toggles, quick search,
 * dimensions), and `editFeedSuccess` patches it from the write's own response,
 * so nothing ever re-reads the entity. It is a client working copy.
 *
 * A service rather than a hook because none of the callers is a rendering
 * component - a resolver, an effect and a dialog - and each of them needs the
 * current user id, which the api layer deliberately does not know.
 */
@Injectable({ providedIn: 'root' })
export class FeedEntityService {
  private readonly userApi = inject(UserApiService);
  private readonly store = inject(Store);

  /**
   * One feed for the current user.
   *
   * `resolveWorkspace` is asked for by the feed-results preview dialog only, so
   * it can name the feed's workspace in its breadcrumb.
   */
  getFeed(feedId: string, resolveWorkspace = false): Observable<Feed> {
    return this.store.select(selectUser).pipe(
      take(1),
      switchMap((user) =>
        this.userApi.getFeed(user.id, feedId, resolveWorkspace),
      ),
    );
  }
}
